import { useEffect, useState } from 'react';

import { Linkedin01Icon, Mail01Icon, ShieldEnergyIcon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import InputValidator from 'helpers/input-validator';

import { FormResponse } from 'components/Form/FormResponse/FormResponse';
import { FormMessage, MessageType } from 'components/FormComponents';
import { Button } from 'components/FormComponents/Button/Button';

import { useGetRefereeResponseQuery } from 'api/referees-api';

import { RefereeMetadataDetailsModal } from '../RefereeMetadataDetailsModal/RefereeMetadataDetailsModal';
import { Statuses, VerificationCard } from './../VerificationCard/VerificationCard';

export const RefereeReport = ({ candidate, referee, referenceCheck, setLoading = () => {} }) => {
  const { data: response, isLoading } = useGetRefereeResponseQuery(referee.id);

  const [emailVerification, setEmailVerification] = useState({ status: '', msg: '' });
  const [linkedinVerification, setLinkedinVerification] = useState({ status: '', msg: '' });
  const [ipVerification, setIpVerification] = useState({ status: '', msg: '' });
  const [showMetadataDetails, setShowMetadataDetails] = useState(false);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  // Set verification statuses
  useEffect(() => {
    // Email
    if (referee?.email === candidate?.email)
      setEmailVerification({ status: Statuses.Error, msg: 'Email addresses match' });
    else if (InputValidator.CompanyEmailValidator(referee?.email))
      setEmailVerification({ status: Statuses.Success, msg: 'Registered Company Email' });
    else setEmailVerification({ status: Statuses.Warn, msg: 'Non company mail' });

    // Linkedin
    if (referee?.linkedin_profile)
      setLinkedinVerification({ status: Statuses.Success, msg: 'Verified Linkedin' });
    else setLinkedinVerification({ status: Statuses.Warn, msg: 'No information provided' });

    // IP
    if (referee?.ip === candidate?.ip)
      setIpVerification({ status: Statuses.Error, msg: 'IP address matches candidate' });
    else setIpVerification({ status: Statuses.Success, msg: 'Unique IP Address' });
  }, [candidate, referee]);

  return (
    <div>
      <div className="flex flex-col px-8 pt-8 pb-0 gap-8 bg-slate-50">
        {ipVerification.status === Statuses.Error && (
          <FormMessage
            closeable={false}
            noMargin
            type={MessageType.Error}
            message="IP address matches candidate. This may indicate the reference was completed on the same device or network."
          />
        )}
        <div className="flex items-center w-full gap-8">
          <VerificationCard>
            <VerificationCard.Header icon={Mail01Icon} hugeicons>
              email address
            </VerificationCard.Header>
            <VerificationCard.Link href={`mailto:${referee.email}`}>
              {referee.email}
            </VerificationCard.Link>
            <VerificationCard.AlertMessage status={emailVerification.status}>
              {emailVerification.msg}
            </VerificationCard.AlertMessage>
          </VerificationCard>
          <VerificationCard>
            <VerificationCard.Header icon={Linkedin01Icon} hugeicons>
              linkedin profile
            </VerificationCard.Header>
            <VerificationCard.Value>
              {referee.linkedin_profile ? referee.linkedin_profile : 'N/A'}
            </VerificationCard.Value>
            <VerificationCard.AlertMessage status={linkedinVerification.status}>
              {linkedinVerification.msg}
            </VerificationCard.AlertMessage>
          </VerificationCard>
          <VerificationCard>
            <VerificationCard.Header icon={ShieldEnergyIcon} hugeicons>
              ip address
            </VerificationCard.Header>
            <VerificationCard.Value>{referee.ip}</VerificationCard.Value>
            <VerificationCard.AlertMessage status={ipVerification.status}>
              {ipVerification.msg}
            </VerificationCard.AlertMessage>
            {referenceCheck.ip && (
              <VerificationCard.Value>
                <Button
                  type="link"
                  className="u-font-size-x-small"
                  onClick={() => setShowMetadataDetails(true)}>
                  View Metadata
                </Button>
              </VerificationCard.Value>
            )}
          </VerificationCard>
        </div>
      </div>

      <div className="border-b border-neutral-4">
        <FormResponse
          form={response?.form}
          answers={response?.answers}
          flagSeverities={response?.flag_severities}
          params={{
            candidate: candidate.first_name,
            candidateFirstName: candidate.first_name,
            candidateFullName: candidate.name,
            candidateLastName: candidate.last_name,
            referee: referee.first_name,
            refereeFirstName: referee.first_name,
            refereeFullName: referee.name,
            refereeLastName: referee.last_name,
            job: candidate.job_role,
            department: candidate.department,
          }}
        />
      </div>

      <RefereeMetadataDetailsModal
        candidate={candidate}
        referee={referee}
        referenceCheck={referenceCheck}
        visible={showMetadataDetails}
        onClose={() => setShowMetadataDetails(false)}
      />
    </div>
  );
};
